import type { IModelConnection } from "@itwin/core-frontend";
import { QueryBinder, QueryRowFormat } from "@itwin/core-common";

// CSI implementation

// DefinitionName constants that match ORD:
//
// Node\ElectricalNode\Lighting\Equipment Pad B
// Node\ElectricalNode\Lighting\HandHole-DHVT
// Node\ElectricalNode\Lighting\HandHole-NDHVT
// Node\ElectricalNode\Lighting\Light Unit Type 9-40
// Node\ElectricalNode\Lighting\Light Unit Type 9-49
// Node\ElectricalNode\Lighting\Light Unit Type Spec1
// Link\ElectricalSegment\Lighting\Non-Metallic Conduit (Lighting)

const DEF_EQUIPMENT_PAD_B = "Node\\ElectricalNode\\Lighting\\Equipment Pad B";
const DEF_HANDHOLE_DHVT = "Node\\ElectricalNode\\Lighting\\HandHole-DHVT";
const DEF_HANDHOLE_NDHVT = "Node\\ElectricalNode\\Lighting\\HandHole-NDHVT";
const DEF_LIGHT_UNIT_9_40 = "Node\\ElectricalNode\\Lighting\\Light Unit Type 9-40";
const DEF_LIGHT_UNIT_9_49 = "Node\\ElectricalNode\\Lighting\\Light Unit Type 9-49";
const DEF_LIGHT_UNIT_SPEC1 = "Node\\ElectricalNode\\Lighting\\Light Unit Type Spec1";
const DEF_CONDUIT_NON_METALLIC_LIGHTING =
  "Conduit\\ElectricalSegment\\Lighting\\Non-Metallic Conduit (Lighting)";
const featureAspectSourceCache = new WeakMap<IModelConnection, FeatureAspectSource>();

interface FeatureAspectSource {
  schemaName: string;
  className: string;
  qualifiedClassName: string;
  selectorPropertyName: string;
  secondarySelectorPropertyName?: string;
}

const FEATURE_ASPECT_SOURCE_CANDIDATES: FeatureAspectSource[] = [
  {
    schemaName: "CifCommon",
    className: "FeatureWithNameAspect",
    qualifiedClassName: "CifCommon.FeatureWithNameAspect",
    selectorPropertyName: "DefinitionName",
  },
  {
    schemaName: "CivilSchema_Published",
    className: "FeatureWithName_PresentationElementAspect",
    qualifiedClassName: "CivilSchema_Published.FeatureWithName_PresentationElementAspect",
    selectorPropertyName: "FeatureName",
    secondarySelectorPropertyName: "ObjectSettings",
  },
];

export interface DefinitionName3dLinkResult {
  definitionName: string;
  // Primary feature elements. If explicit 3d->3d representation links exist,
  // these are the relationship targets. Otherwise this falls back to all feature 3d ids.
  primaryElementIds: string[];
  // Relationship map: primary element id -> linked graphic/source ids.
  relatedGraphicIdsByPrimary: Record<string, string[]>;
  // Flat list of all linked graphics (deduped).
  relatedGraphicIds: string[];
  // All 3d elements that match the feature definition before link shaping.
  matchedElementIds: string[];
}

export interface DefinitionName2dLinkResult {
  definitionName: string;
  // 2D graphics that represent feature elements with this DefinitionName.
  relatedGraphicIds: string[];
  // Related graphics split by actual element dimensionality. Some "2D" symbolic
  // links are stored as GeometricElement3d in the model.
  relatedGraphic2dIds: string[];
  relatedGraphic3dIds: string[];
  // Feature elements matched by DefinitionName (targets of representation links).
  matchedElementIds: string[];
}

export interface ElectricalDefinitionNameDiagnostic {
  definitionName: string;
  topology: "Node" | "Link" | "Other";
  group: string;
  elementCount: number;
  has3dGeometryCount: number;
  has2dGeometryCount: number;
}

function electricalNodeDefinitionLeafName(definitionName: string): string {
  const parts = String(definitionName ?? "")
    .split("\\")
    .map((part) => part.trim())
    .filter(Boolean);
  return parts.length > 0 ? parts[parts.length - 1] : "";
}

function escapeForLike(value: string): string {
  return String(value ?? "").replace(/'/g, "''");
}

async function resolveFeatureAspectSource(iModel: IModelConnection): Promise<FeatureAspectSource> {
  const cached = featureAspectSourceCache.get(iModel);
  if (cached) return cached;

  for (const candidate of FEATURE_ASPECT_SOURCE_CANDIDATES) {
    const reader = iModel.createQueryReader(
      `
        SELECT c.Name AS className
        FROM meta.ECClassDef c
        JOIN meta.ECSchemaDef s
          ON s.ECInstanceId = c.Schema.Id
        WHERE s.Name = ?
          AND c.Name = ?
      `,
      QueryBinder.from([candidate.schemaName, candidate.className]),
      { rowFormat: QueryRowFormat.UseJsPropertyNames }
    );

    for await (const row of reader) {
      if (String((row as any)?.className ?? "").trim()) {
        featureAspectSourceCache.set(iModel, candidate);
        return candidate;
      }
    }
  }

  throw new Error(
    "No supported feature aspect class was found. Expected CifCommon.FeatureWithNameAspect or CivilSchema_Published.FeatureWithName_PresentationElementAspect."
  );
}

function definitionNameVariants(name: string): string[] {
  const base = String(name ?? "").trim();
  if (!base) return [];
  const variants = new Set<string>();
  variants.add(base);
  variants.add(base.replace(/\\\\/g, "\\"));
  variants.add(base.replace(/\\/g, "\\\\"));
  return [...variants].filter(Boolean);
}

function buildFeatureAspectSelectorValues(definitionName: string, featureAspectSource: FeatureAspectSource): string[] {
  const baseDefinitionName = String(definitionName ?? "").trim();
  if (!baseDefinitionName) return [];

  if (featureAspectSource.selectorPropertyName === "DefinitionName") {
    return definitionNameVariants(baseDefinitionName);
  }

  const selectorValues = new Set<string>();
  const leafName = electricalNodeDefinitionLeafName(baseDefinitionName);
  if (leafName) selectorValues.add(leafName);
  selectorValues.add(baseDefinitionName);
  return [...selectorValues].filter(Boolean);
}

function buildFeatureAspectSecondaryPatterns(
  definitionName: string,
  featureAspectSource: FeatureAspectSource
): string[] {
  if (!featureAspectSource.secondarySelectorPropertyName) return [];

  const baseDefinitionName = String(definitionName ?? "").trim();
  if (!baseDefinitionName) return [];

  const leafName = electricalNodeDefinitionLeafName(baseDefinitionName);
  const strippedLeafName = String(leafName).replace(/\s*\((Lighting|Signal)\)\s*$/i, "").trim();
  const strippedDefinitionName = String(baseDefinitionName)
    .replace(/\s*\((Lighting|Signal)\)\s*$/i, "")
    .trim();

  return [
    `%${escapeForLike(baseDefinitionName)}%`,
    leafName ? `%${escapeForLike(leafName)}%` : "",
    strippedLeafName ? `%${escapeForLike(strippedLeafName)}%` : "",
    strippedDefinitionName ? `%${escapeForLike(strippedDefinitionName)}%` : "",
  ].filter(Boolean);
}

/**
 * 3D-only helper: given a DefinitionName, return unique ECInstanceIds
 * of GeometricElement3d elements that actually have geometry.
 */
async function query3dElementsByDefinitionName(
  iModel: IModelConnection,
  definitionName: string
): Promise<string[]> {
  const ids = new Set<string>();
  const featureAspectSource = await resolveFeatureAspectSource(iModel);

  for (const selectorValue of buildFeatureAspectSelectorValues(definitionName, featureAspectSource)) {
    const ecsql = `
      SELECT g.ECInstanceId AS id
      FROM ${featureAspectSource.qualifiedClassName} f
      JOIN BisCore.GeometricElement3d g
        ON g.ECInstanceId = f.Element.Id
      WHERE f.${featureAspectSource.selectorPropertyName} = ?
        AND g.GeometryStream IS NOT NULL
    `;

    const reader = iModel.createQueryReader(ecsql, QueryBinder.from([selectorValue]), {
      rowFormat: QueryRowFormat.UseJsPropertyNames,
    });

    for await (const row of reader) {
      const r = row as any;
      if (!r.id) continue;
      ids.add(r.id);
    }

    if (ids.size > 0) break;
  }

  if (ids.size === 0 && featureAspectSource.secondarySelectorPropertyName) {
    for (const pattern of buildFeatureAspectSecondaryPatterns(definitionName, featureAspectSource)) {
      const ecsql = `
        SELECT g.ECInstanceId AS id
        FROM ${featureAspectSource.qualifiedClassName} f
        JOIN BisCore.GeometricElement3d g
          ON g.ECInstanceId = f.Element.Id
        WHERE f.${featureAspectSource.secondarySelectorPropertyName} LIKE ?
          AND g.GeometryStream IS NOT NULL
      `;

      const reader = iModel.createQueryReader(ecsql, QueryBinder.from([pattern]), {
        rowFormat: QueryRowFormat.UseJsPropertyNames,
      });

      for await (const row of reader) {
        const r = row as any;
        if (!r.id) continue;
        ids.add(r.id);
      }

      if (ids.size > 0) break;
    }
  }

  return [...ids];
}

export function queryElements3dByDefinitionName(
  iModel: IModelConnection,
  definitionName: string
): Promise<string[]> {
  return query3dElementsByDefinitionName(iModel, definitionName);
}

/**
 * Returns primary 3d feature elements and their linked 3d graphics via
 * BisCore.GraphicalElement3dRepresentsElement.
 */
export async function query3dFeatureElementsWithRepresentations(
  iModel: IModelConnection,
  definitionName: string
): Promise<DefinitionName3dLinkResult> {
  const matchedElementIds = await query3dElementsByDefinitionName(iModel, definitionName);

  const relationRows: Array<{ sourceId: string; targetId: string }> = [];
  const featureAspectSource = await resolveFeatureAspectSource(iModel);

  for (const selectorValue of buildFeatureAspectSelectorValues(definitionName, featureAspectSource)) {
    const relationQuery = `
      SELECT rel.SourceECInstanceId AS sourceId, rel.TargetECInstanceId AS targetId
      FROM BisCore.GraphicalElement3dRepresentsElement rel
      JOIN ${featureAspectSource.qualifiedClassName} f
        ON f.Element.Id = rel.TargetECInstanceId
      JOIN BisCore.GeometricElement3d g
        ON g.ECInstanceId = f.Element.Id
      WHERE f.${featureAspectSource.selectorPropertyName} = ?
        AND g.GeometryStream IS NOT NULL
    `;

    const relationReader = iModel.createQueryReader(
      relationQuery,
      QueryBinder.from([selectorValue]),
      {
        rowFormat: QueryRowFormat.UseJsPropertyNames,
      }
    );

    for await (const row of relationReader) {
      const r = row as any;
      if (!r?.sourceId || !r?.targetId) continue;
      relationRows.push({ sourceId: r.sourceId, targetId: r.targetId });
    }

    if (relationRows.length > 0) break;
  }

  const targetIds = new Set<string>();
  const sourceIds = new Set<string>();
  const relatedGraphicIdsByPrimary: Record<string, string[]> = {};

  for (const rel of relationRows) {
    targetIds.add(rel.targetId);
    sourceIds.add(rel.sourceId);

    const bucket = relatedGraphicIdsByPrimary[rel.targetId] ?? [];
    if (!bucket.includes(rel.sourceId)) {
      bucket.push(rel.sourceId);
      relatedGraphicIdsByPrimary[rel.targetId] = bucket;
    }
  }

  const primarySet = new Set<string>();

  if (targetIds.size > 0) {
    for (const targetId of targetIds) primarySet.add(targetId);

    // Keep unmatched feature elements as primaries too (for cases where some elements
    // have no explicit representation links).
    for (const id of matchedElementIds) {
      if (!targetIds.has(id) && !sourceIds.has(id)) {
        primarySet.add(id);
      }
    }
  } else {
    for (const id of matchedElementIds) primarySet.add(id);
  }

  const primaryElementIds = [...primarySet];
  const relatedGraphicIds = [...sourceIds];

  return {
    definitionName,
    primaryElementIds,
    relatedGraphicIdsByPrimary,
    relatedGraphicIds,
    matchedElementIds,
  };
}

async function query2dElementsByDefinitionName(
  iModel: IModelConnection,
  definitionName: string
): Promise<string[]> {
  const ids = new Set<string>();
  const featureAspectSource = await resolveFeatureAspectSource(iModel);

  for (const selectorValue of buildFeatureAspectSelectorValues(definitionName, featureAspectSource)) {
    const ecsql = `
      SELECT g.ECInstanceId AS id
      FROM ${featureAspectSource.qualifiedClassName} f
      JOIN BisCore.GeometricElement2d g
        ON g.ECInstanceId = f.Element.Id
      WHERE f.${featureAspectSource.selectorPropertyName} = ?
        AND g.GeometryStream IS NOT NULL
    `;

    const reader = iModel.createQueryReader(ecsql, QueryBinder.from([selectorValue]), {
      rowFormat: QueryRowFormat.UseJsPropertyNames,
    });

    for await (const row of reader) {
      const r = row as any;
      if (!r.id) continue;
      ids.add(r.id);
    }

    if (ids.size > 0) break;
  }

  return [...ids];
}

export function queryElements2dByDefinitionName(
  iModel: IModelConnection,
  definitionName: string
): Promise<string[]> {
  return query2dElementsByDefinitionName(iModel, definitionName);
}

/**
 * Returns 2D graphical representation elements for feature elements matching DefinitionName
 * via BisCore.GraphicalElement2dRepresentsElement.
 */
export async function query2dGraphicsForFeatureDefinition(
  iModel: IModelConnection,
  definitionName: string
): Promise<DefinitionName2dLinkResult> {
  const relatedGraphicIds = new Set<string>();
  const relatedGraphic2dIds = new Set<string>();
  const relatedGraphic3dIds = new Set<string>();
  const matchedFeatureIds = new Set<string>();

  // Use the BisCore base relationship to avoid hard-coding schema-specific
  // subclasses that may not exist in all iModel versions.
  // Some models wire feature<->graphic links in the opposite direction, so query
  // both shapes and normalize them into (graphicId, featureId).
  try {
    const featureAspectSource = await resolveFeatureAspectSource(iModel);
    for (const selectorValue of buildFeatureAspectSelectorValues(definitionName, featureAspectSource)) {
      const relationQuery = `
        SELECT DISTINCT
          rel.SourceECInstanceId AS graphicId,
          rel.TargetECInstanceId AS featureId,
          CASE WHEN g2.ECInstanceId IS NOT NULL THEN 1 ELSE 0 END AS is2dGraphic,
          CASE WHEN g3.ECInstanceId IS NOT NULL THEN 1 ELSE 0 END AS is3dGraphic
        FROM BisCore.ElementRefersToElements rel
        JOIN ${featureAspectSource.qualifiedClassName} f
          ON f.Element.Id = rel.TargetECInstanceId
        LEFT JOIN BisCore.GeometricElement2d g2
          ON g2.ECInstanceId = rel.SourceECInstanceId
        LEFT JOIN BisCore.GeometricElement3d g3
          ON g3.ECInstanceId = rel.SourceECInstanceId
        WHERE f.${featureAspectSource.selectorPropertyName} = ?
          AND (g2.GeometryStream IS NOT NULL OR g3.GeometryStream IS NOT NULL)
        UNION
        SELECT DISTINCT
          rel.TargetECInstanceId AS graphicId,
          rel.SourceECInstanceId AS featureId,
          CASE WHEN g2.ECInstanceId IS NOT NULL THEN 1 ELSE 0 END AS is2dGraphic,
          CASE WHEN g3.ECInstanceId IS NOT NULL THEN 1 ELSE 0 END AS is3dGraphic
        FROM BisCore.ElementRefersToElements rel
        JOIN ${featureAspectSource.qualifiedClassName} f
          ON f.Element.Id = rel.SourceECInstanceId
        LEFT JOIN BisCore.GeometricElement2d g2
          ON g2.ECInstanceId = rel.TargetECInstanceId
        LEFT JOIN BisCore.GeometricElement3d g3
          ON g3.ECInstanceId = rel.TargetECInstanceId
        WHERE f.${featureAspectSource.selectorPropertyName} = ?
          AND (g2.GeometryStream IS NOT NULL OR g3.GeometryStream IS NOT NULL)
      `;

      const relationReader = iModel.createQueryReader(
        relationQuery,
        QueryBinder.from([selectorValue, selectorValue]),
        {
          rowFormat: QueryRowFormat.UseJsPropertyNames,
        }
      );

      for await (const row of relationReader) {
        const r = row as any;
        if (!r?.graphicId || !r?.featureId) continue;
        relatedGraphicIds.add(r.graphicId);
        const is2dGraphic = Number(r.is2dGraphic ?? 0) !== 0;
        const is3dGraphic = Number(r.is3dGraphic ?? 0) !== 0;
        if (is2dGraphic) relatedGraphic2dIds.add(r.graphicId);
        if (is3dGraphic) relatedGraphic3dIds.add(r.graphicId);
        matchedFeatureIds.add(r.featureId);
      }

      if (relatedGraphicIds.size > 0) break;
    }
  } catch (err) {
    // Some models/schemas may not expose the expected relationship shape.
    // Caller falls back to direct DefinitionName lookup.
    void err;
  }

  return {
    definitionName,
    relatedGraphicIds: [...relatedGraphicIds],
    relatedGraphic2dIds: [...relatedGraphic2dIds],
    relatedGraphic3dIds: [...relatedGraphic3dIds],
    matchedElementIds: [...matchedFeatureIds],
  };
}

/**
 * Diagnostic helper: find candidate DefinitionName values that match a pattern.
 * This helps when the exact DefinitionName is unknown or differs in the model.
 */
async function queryDefinitionNamesLike(
  iModel: IModelConnection,
  pattern: string,
  limit = 20
): Promise<string[]> {
  const names: string[] = [];
  const featureAspectSource = await resolveFeatureAspectSource(iModel);
  const selectorPredicate = featureAspectSource.secondarySelectorPropertyName
    ? `(f.${featureAspectSource.selectorPropertyName} LIKE ? OR f.${featureAspectSource.secondarySelectorPropertyName} LIKE ?)`
    : `f.${featureAspectSource.selectorPropertyName} LIKE ?`;
  const ecsql = `
    SELECT DISTINCT f.${featureAspectSource.selectorPropertyName} AS name
    FROM ${featureAspectSource.qualifiedClassName} f
    LEFT JOIN BisCore.GeometricElement3d g3
      ON g3.ECInstanceId = f.Element.Id
    LEFT JOIN BisCore.GeometricElement2d g2
      ON g2.ECInstanceId = f.Element.Id
    WHERE ${selectorPredicate}
      AND (g3.GeometryStream IS NOT NULL OR g2.GeometryStream IS NOT NULL)
  `;

  const bindValues = featureAspectSource.secondarySelectorPropertyName ? [pattern, pattern] : [pattern];
  const reader = iModel.createQueryReader(ecsql, QueryBinder.from(bindValues), {
    rowFormat: QueryRowFormat.UseJsPropertyNames,
  });

  for await (const row of reader) {
    const r = row as any;
    if (!r.name) continue;
    names.push(r.name);
    if (names.length >= limit) break;
  }

  return names;
}

async function query3dElementsByDefinitionCandidates(
  iModel: IModelConnection,
  preferredDefinitionName: string,
  candidatePatterns: string[]
): Promise<string[]> {
  const ids = new Set<string>();

  for (const id of await query3dElementsByDefinitionName(iModel, preferredDefinitionName)) {
    ids.add(id);
  }
  if (ids.size > 0) {
    return [...ids];
  }

  const candidateNames = new Set<string>();
  for (const pattern of candidatePatterns) {
    for (const name of await queryDefinitionNamesLike(iModel, pattern, 50)) {
      candidateNames.add(name);
    }
  }

  for (const candidateName of candidateNames) {
    for (const id of await query3dElementsByDefinitionName(iModel, candidateName)) {
      ids.add(id);
    }
  }

  return [...ids];
}

export function queryDefinitionNamesLikeFromModel(
  iModel: IModelConnection,
  pattern: string,
  limit = 20
): Promise<string[]> {
  return queryDefinitionNamesLike(iModel, pattern, limit);
}

export async function queryElectricalDefinitionNameDiagnostics(
  iModel: IModelConnection,
  limit = 200
): Promise<ElectricalDefinitionNameDiagnostic[]> {
  const rows: ElectricalDefinitionNameDiagnostic[] = [];
  const featureAspectSource = await resolveFeatureAspectSource(iModel);

  const ecsql = `
    SELECT
      f.${featureAspectSource.selectorPropertyName} AS definitionName,
      COUNT(DISTINCT f.Element.Id) AS elementCount,
      COUNT(DISTINCT CASE WHEN g3.GeometryStream IS NOT NULL THEN f.Element.Id END) AS has3dGeometryCount,
      COUNT(DISTINCT CASE WHEN g2.GeometryStream IS NOT NULL THEN f.Element.Id END) AS has2dGeometryCount
    FROM ${featureAspectSource.qualifiedClassName} f
    LEFT JOIN BisCore.GeometricElement3d g3
      ON g3.ECInstanceId = f.Element.Id
    LEFT JOIN BisCore.GeometricElement2d g2
      ON g2.ECInstanceId = f.Element.Id
    WHERE
      f.${featureAspectSource.selectorPropertyName} LIKE 'Node\\ElectricalNode\\%'
      OR f.${featureAspectSource.selectorPropertyName} LIKE 'Link\\ElectricalSegment\\%'
      OR f.${featureAspectSource.selectorPropertyName} LIKE 'Conduit\\ElectricalSegment\\%'
      OR f.${featureAspectSource.selectorPropertyName} LIKE '%Light%'
      OR f.${featureAspectSource.selectorPropertyName} LIKE '%Conduit%'
    GROUP BY f.${featureAspectSource.selectorPropertyName}
    ORDER BY f.${featureAspectSource.selectorPropertyName}
  `;

  const reader = iModel.createQueryReader(ecsql, undefined, {
    rowFormat: QueryRowFormat.UseJsPropertyNames,
  });

  for await (const row of reader) {
    const r = row as any;
    const definitionName = String(r.definitionName ?? "").trim();
    if (!definitionName) continue;

    const topology = definitionName.startsWith("Node\\")
      ? "Node"
      : definitionName.startsWith("Link\\") || definitionName.startsWith("Conduit\\")
        ? "Link"
        : "Other";
    const parts = definitionName.split("\\");
    const group = parts.length >= 3 ? parts[2] : "";

    rows.push({
      definitionName,
      topology,
      group,
      elementCount: Number(r.elementCount ?? 0),
      has3dGeometryCount: Number(r.has3dGeometryCount ?? 0),
      has2dGeometryCount: Number(r.has2dGeometryCount ?? 0),
    });

    if (rows.length >= limit) break;
  }

  return rows;
}

// Thin wrappers per feature type - line up with your utilityFeatureTypes.

export function queryEquipmentPadB(iModel: IModelConnection): Promise<string[]> {
  return query3dElementsByDefinitionName(iModel, DEF_EQUIPMENT_PAD_B);
}

export function queryHandholeDhvt(iModel: IModelConnection): Promise<string[]> {
  return query3dElementsByDefinitionName(iModel, DEF_HANDHOLE_DHVT);
}

export function queryHandholeNdhvt(iModel: IModelConnection): Promise<string[]> {
  return query3dElementsByDefinitionName(iModel, DEF_HANDHOLE_NDHVT);
}

export function queryLightUnitType9_40(iModel: IModelConnection): Promise<string[]> {
  return query3dElementsByDefinitionCandidates(iModel, DEF_LIGHT_UNIT_9_40, [
    "%Light Unit Type 9-40%",
    "%9-40%",
  ]);
}

export function queryLightUnitType9_49(iModel: IModelConnection): Promise<string[]> {
  return query3dElementsByDefinitionName(iModel, DEF_LIGHT_UNIT_9_49);
}

export function queryLightUnitTypeSpec1(iModel: IModelConnection): Promise<string[]> {
  return query3dElementsByDefinitionName(iModel, DEF_LIGHT_UNIT_SPEC1);
}

export async function queryNonMetallicConduitLighting(
  iModel: IModelConnection
): Promise<string[]> {
  const ids = await query3dElementsByDefinitionName(
    iModel,
    DEF_CONDUIT_NON_METALLIC_LIGHTING
  );

  if (ids.length === 0) {
    const ids2d = await query2dElementsByDefinitionName(
      iModel,
      DEF_CONDUIT_NON_METALLIC_LIGHTING
    );
    if (ids2d.length > 0) return ids2d;

    const candidates = await queryDefinitionNamesLike(iModel, "%Conduit%Lighting%", 20);
    void candidates;
  }

  return ids;
}
